import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SelectWithSearch from "@/features/shared/components/SelectWithSearch/SelectWithSearch";
import useClients from "@/features/client/hooks/useClients";
import { toast } from "react-toastify";
const ClientSelector = ({ onSelect, initialValue }) => {
    const { clients, handleAddClient } = useClients(undefined, undefined, true);
    const [selectedClient, setSelectedClient] = useState(initialValue ? {
        value: initialValue.id ?? 0,
        label: initialValue.denomination?.trim() || "",
    } : { value: 0, label: "" });
    const [localClients, setLocalClients] = useState(clients);
    const createdClientRef = useRef(null);
    useEffect(() => {
        setLocalClients(clients);
    }, [clients]);
    useEffect(() => {
        if (initialValue) {
            setSelectedClient({
                value: initialValue.id ?? 0,
                label: initialValue.denomination?.trim() || "",
            });
            setLocalClients((prev) => {
                const exists = prev.some((client) => client.id === initialValue.id);
                return exists ? prev : [...prev, initialValue];
            });
        }
    }, [initialValue]);
    const clientOptions = useMemo(() => localClients.map((c) => ({
        value: c.id ?? 0,
        label: c.denomination?.trim() || "",
    })), [localClients]);
    const handleClientSelect = useCallback((option) => {
        const selected = localClients.find((c) => c.id === option.value) ||
            (createdClientRef.current && createdClientRef.current.id === option.value
                ? createdClientRef.current
                : undefined);
        setSelectedClient({ value: option.value, label: option.label });
        if (selected) {
            onSelect(selected);
        }
        else {
            onSelect({ id: option.value, denomination: option.label });
        }
        if (createdClientRef.current && createdClientRef.current.id === option.value) {
            createdClientRef.current = null;
        }
    }, [localClients, onSelect]);
    const handleCreateClient = useCallback(async (label) => {
        const trimmedLabel = label.trim();
        if (!trimmedLabel) {
            return null;
        }
        const existing = localClients.find((client) => client.denomination?.toLowerCase() === trimmedLabel.toLowerCase());
        if (existing) {
            const option = { value: existing.id ?? 0, label: existing.denomination?.trim() || trimmedLabel };
            handleClientSelect(option);
            return option;
        }
        try {
            const newClient = await handleAddClient({
                denomination: trimmedLabel,
                indic_actif: true,
            });
            if (!newClient) {
                return null;
            }
            const normalizedClient = {
                ...newClient,
                denomination: newClient.denomination ?? trimmedLabel,
            };
            setLocalClients((prev) => {
                const exists = prev.some((client) => client.id === normalizedClient.id);
                return exists ? prev : [...prev, normalizedClient];
            });
            createdClientRef.current = normalizedClient;
            toast.success(`Client "${normalizedClient.denomination}" ajouté.`);
            return {
                value: normalizedClient.id ?? 0,
                label: normalizedClient.denomination?.trim() || trimmedLabel,
            };
        }
        catch (error) {
            console.error("Erreur lors de la création du client :", error);
            toast.error(error?.message ?? "Erreur inattendue lors de la création du client.");
            return null;
        }
    }, [handleAddClient, handleClientSelect, localClients]);
    return (_jsx(SelectWithSearch, { label: "Client", options: clientOptions, selectedItem: selectedClient, onSelect: handleClientSelect, allowCreate: true, onCreate: handleCreateClient, getCreateOptionLabel: (value) => `Créer le client "${value}"` }));
};
export default ClientSelector;
